export const TRAINING_JOB_STATUS_DETAILS = {
  // Dataset preparation
  awaitingManualStart: "awaiting_manual_start",
  exportingEvalDataset: "exporting_eval_dataset",
  exportingTrainingDataset: "exporting_training_dataset",
  datasetsExported: "datasets_exported",
  datasetExportFailed: "dataset_export_failed",
  datasetPrepQueueFailed: "dataset_prep_queue_failed",

  // Launch
  launchClaimed: "launch.claimed",
  providerLaunchSucceeded: "training job deployed to provider",
  manualRuntimeConnected: "manual_runtime_connected",
  timedOutInQueue: "timed out in queue",

  // Runtime lifecycle (reported by container via webhook events)
  gpuPreflightStarted: "job.gpu_preflight.started",
  gpuPreflightCompleted: "job.gpu_preflight.completed",
  artifactDownloadStarted: "job.artifact_download.started",
  artifactDownloadCompleted: "job.artifact_download.completed",
  modelDownloadStarted: "job.model_download.started",
  modelDownloadCompleted: "job.model_download.completed",
  checkpointResumeDownloadStarted: "job.checkpoint_resume_download.started",
  checkpointResumeDownloadCompleted: "job.checkpoint_resume_download.completed",
  torchrunInitStarted: "job.torchrun_init.started",
  trainingStarted: "job.training.started",
  trainingInProgress: "training",
  trainingCompleted: "job.training.completed",
  inferenceExportStarted: "job.inference_export.started",
  inferenceExportCompleted: "job.inference_export.completed",
  checkpointUploadStarted: "job.checkpoint_upload.started",
  checkpointUploadProgress: "job.checkpoint_upload.progress",
  completed: "job.completed",

  // Rollover
  rolloverRequested: "rollover.requested",
  rolloverCompleted: "rollover.completed",
  rolloverFailed: "rollover.failed",

  // Watchdog timeout reasons
  startupTimeout: "startup_timeout",
  maxRuntimeExceeded: "max_runtime_exceeded",
  heartbeatStale: "heartbeat_stale",
  torchrunInitTimeout: "torchrun_init_timeout",
  launchQueueTimeout: "launch_queue_timeout",
  invalidLaunchState: "invalid_launch_state",

  // Watchdog failure reasons
  workerCountMismatch: "worker_count_mismatch",
  nanLoss: "nan_loss",
  trainingStepStall: "training_step_stall",

  // Terminal
  providerLaunchFailed: "provider_launch_failed",
  cancelledByUser: "cancelled_by_user",
} as const;

/**
 * Human-readable label overrides for status detail values where
 * auto-humanization (replacing separators + title-casing) would be wrong.
 *
 * Values not in this map are auto-humanized at display time.
 * Runtime error codes from containers (e.g. "nccl_timeout",
 * "CUDA error: device-side assert triggered") are never in this map —
 * they pass through the humanizer as-is.
 */
export const TRAINING_JOB_STATUS_DETAIL_LABELS: Record<string, string> = {
  [TRAINING_JOB_STATUS_DETAILS.providerLaunchSucceeded]:
    "Provider launch succeeded",
  [TRAINING_JOB_STATUS_DETAILS.trainingInProgress]: "Training in progress",
  [TRAINING_JOB_STATUS_DETAILS.timedOutInQueue]: "Timed out in queue",
  [TRAINING_JOB_STATUS_DETAILS.nanLoss]: "NaN loss",
  [TRAINING_JOB_STATUS_DETAILS.gpuPreflightStarted]: "GPU preflight started",
  [TRAINING_JOB_STATUS_DETAILS.gpuPreflightCompleted]:
    "GPU preflight completed",
};

export function formatTrainingJobStatusDetail(detail: string): string {
  if (detail in TRAINING_JOB_STATUS_DETAIL_LABELS) {
    const label = TRAINING_JOB_STATUS_DETAIL_LABELS[detail];
    if (label) return label;
  }

  return detail
    .replace(/[._-]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      if (lower === "gpu") return "GPU";
      if (lower === "nccl") return "NCCL";
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}
